import { Typography, useTheme } from "@mui/material";
import { HStack } from "Components/_Shared/Core/Stack";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as ProgressIcon } from "Components/_Shared/Icons/Assets/progress.svg";
import { ReactComponent as UnavailableIcon } from "Components/_Shared/Icons/Assets/unavailable.svg";
import { SearchedClientAnalysisState } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

type Props = {
  readonly state: SearchedClientAnalysisState;
  readonly size?: "small" | "medium";
  readonly coloredText?: boolean;
};

export const AnalysisStatusLabel: React.FC<Props> = ({ state, size = "medium", coloredText = false }) => {
  const theme = useTheme();
  const { t } = useResource();

  const iconSize = size === "small" ? 14 : 20;
  const colorMap = {
    [SearchedClientAnalysisState.in_progress]: theme.palette.info.main,
    [SearchedClientAnalysisState.completed]: theme.palette.success.main,
    [SearchedClientAnalysisState.declined]: theme.palette.warning.dark,
  };

  const getText = (state: SearchedClientAnalysisState): string => {
    switch (state) {
      case SearchedClientAnalysisState.in_progress:
        return t(Resources.AnalysesListing.Status.InProgress);
      case SearchedClientAnalysisState.completed:
        return t(Resources.AnalysesListing.Status.Completed);
      case SearchedClientAnalysisState.declined:
        return t(Resources.AnalysesListing.Status.Declined);
      default:
        return "";
    }
  };

  const getColor = (state: SearchedClientAnalysisState): string | undefined => {
    switch (state) {
      case SearchedClientAnalysisState.in_progress:
      case SearchedClientAnalysisState.completed:
      case SearchedClientAnalysisState.declined:
        return colorMap[state];
      default:
        return undefined;
    }
  };

  const getIcon = (state: SearchedClientAnalysisState): React.ReactElement | null => {
    switch (state) {
      case SearchedClientAnalysisState.in_progress:
        return <ProgressIcon color={colorMap[state]} width={iconSize} height={iconSize} />;
      case SearchedClientAnalysisState.completed:
        return <CheckIcon color={colorMap[state]} width={iconSize} height={iconSize} />;
      case SearchedClientAnalysisState.declined:
        return <UnavailableIcon color={colorMap[state]} width={iconSize} height={iconSize} />;
      default:
        return null;
    }
  };

  return (
    <HStack spacing={1} alignItems="center" justifyContent={"center"}>
      {getIcon(state)}
      <Typography
        variant="body2"
        fontSize={size === "small" ? 12 : 14}
        lineHeight={"1.5"}
        sx={{ color: coloredText ? getColor(state) : "text.secondary" }}
      >
        {getText(state)}
      </Typography>
    </HStack>
  );
};
